import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';


import Input from '../../components/ui/Input';
import CashDrawerManager from './components/CashDrawerManager';
import ReconciliationForm from './components/ReconciliationForm';
import VarianceAlerts from './components/VarianceAlerts';
import { openaiService } from '../../services/openaiService';
import { emailService } from '../../services/emailService';

const CashDrawerReconciliationCenter = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, loading: authLoading, profileLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [drawers, setDrawers] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [varianceThreshold, setVarianceThreshold] = useState(5);
  const [autoAlertEnabled, setAutoAlertEnabled] = useState(true);
  const [stats, setStats] = useState({
    totalReconciliations: 0,
    totalVariance: 0,
    pendingAlerts: 0,
    averageVariance: 0
  });

  useEffect(() => {
    if (!authLoading && !profileLoading && (!user || (!isGymOwner && !isStaff))) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, isStaff, authLoading, profileLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && (isGymOwner || isStaff) && !profileLoading) {
      loadData();
    }
  }, [currentTenant, isGymOwner, isStaff, profileLoading]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadDrawers(),
        loadReconciliations(),
        loadStats()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDrawers = async () => {
    try {
      const { data, error } = await supabase?.from('cash_drawers')?.select('*')?.eq('tenant_id', currentTenant?.id)?.order('opened_at', { ascending: false });

      if (error) throw error;
      setDrawers(data || []);

      // Set active drawer if exists
      const active = data?.find(d => d?.status === 'open');
      if (active) setActiveDrawer(active);
    } catch (error) {
      console.error('Error loading drawers:', error);
    }
  };

  const loadReconciliations = async () => {
    try {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      const { data, error } = await supabase?.from('cash_reconciliations')?.select(`
          *,
          cash_drawers(id, name, opened_at, closed_at),
          user_profiles:reconciled_by(full_name, email)
        `)?.eq('tenant_id', currentTenant?.id)?.gte('created_at', today?.toISOString())?.order('created_at', { ascending: false });

      if (error) throw error;
      setReconciliations(data || []);

      // Check for variances and trigger alerts
      if (autoAlertEnabled) {
        await checkVarianceAlerts(data);
      }
    } catch (error) {
      console.error('Error loading reconciliations:', error);
    }
  };

  const loadStats = async () => {
    try {
      const { data, error } = await supabase?.from('cash_reconciliations')?.select('*')?.eq('tenant_id', currentTenant?.id);

      if (error) throw error;

      const totalVariance = data?.reduce((sum, r) => {
        const variance = parseFloat(r?.actual_balance || 0) - parseFloat(r?.expected_balance || 0);
        return sum + Math.abs(variance);
      }, 0);

      const pendingAlerts = data?.filter(r => {
        const variance = Math.abs(parseFloat(r?.actual_balance || 0) - parseFloat(r?.expected_balance || 0));
        return variance > varianceThreshold && !r?.alert_sent;
      })?.length;

      setStats({
        totalReconciliations: data?.length || 0,
        totalVariance,
        pendingAlerts,
        averageVariance: data?.length > 0 ? totalVariance / data?.length : 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const checkVarianceAlerts = async (reconciliations) => {
    try {
      for (const reconciliation of reconciliations) {
        const variance = Math.abs(
          parseFloat(reconciliation?.actual_balance || 0) - 
          parseFloat(reconciliation?.expected_balance || 0)
        );

        if (variance > varianceThreshold && !reconciliation?.alert_sent) {
          // Trigger AI analysis
          const aiAnalysis = await analyzeVariance(reconciliation);

          // Send email alert
          await sendVarianceAlert(reconciliation, aiAnalysis);

          // Mark alert as sent
          await supabase?.from('cash_reconciliations')?.update({ 
              alert_sent: true,
              ai_analysis: aiAnalysis
            })?.eq('id', reconciliation?.id);
        }
      }
    } catch (error) {
      console.error('Error checking variance alerts:', error);
    }
  };

  const analyzeVariance = async (reconciliation) => {
    try {
      const variance = parseFloat(reconciliation?.actual_balance || 0) - parseFloat(reconciliation?.expected_balance || 0);
      
      const prompt = `Analyze this cash drawer variance:
      Expected: $${reconciliation?.expected_balance}
      Actual: $${reconciliation?.actual_balance}
      Variance: $${variance}
      Reason provided: ${reconciliation?.variance_reason || 'None'}
      
      Provide:
      1. Risk level (low/medium/high/critical)
      2. Possible causes
      3. Recommended actions
      4. Any suspicious patterns
      
      Format as JSON with keys: riskLevel, causes, recommendations, suspiciousPatterns`;

      const analysis = await openaiService?.generateCompletion(prompt);
      return JSON.parse(analysis);
    } catch (error) {
      console.error('Error analyzing variance:', error);
      return {
        riskLevel: 'unknown',
        causes: ['Unable to analyze'],
        recommendations: ['Manual review required'],
        suspiciousPatterns: []
      };
    }
  };

  const sendVarianceAlert = async (reconciliation, aiAnalysis) => {
    try {
      const variance = parseFloat(reconciliation?.actual_balance || 0) - parseFloat(reconciliation?.expected_balance || 0);
      
      await emailService?.sendEmail({
        to: currentTenant?.owner_email || user?.email,
        subject: `Cash Variance Alert: $${Math.abs(variance)?.toFixed(2)}`,
        html: `
          <h2>Cash Drawer Variance Detected</h2>
          <p><strong>Drawer:</strong> ${reconciliation?.cash_drawers?.name || 'Unknown'}</p>
          <p><strong>Expected Balance:</strong> $${reconciliation?.expected_balance}</p>
          <p><strong>Actual Balance:</strong> $${reconciliation?.actual_balance}</p>
          <p><strong>Variance:</strong> ${variance >= 0 ? '+' : ''}$${variance?.toFixed(2)}</p>
          <p><strong>Risk Level:</strong> ${aiAnalysis?.riskLevel?.toUpperCase()}</p>
          <h3>AI Analysis:</h3>
          <p><strong>Possible Causes:</strong></p>
          <ul>${aiAnalysis?.causes?.map(c => `<li>${c}</li>`)?.join('')}</ul>
          <p><strong>Recommendations:</strong></p>
          <ul>${aiAnalysis?.recommendations?.map(r => `<li>${r}</li>`)?.join('')}</ul>
          ${aiAnalysis?.suspiciousPatterns?.length > 0 ? `
            <p><strong>⚠️ Suspicious Patterns:</strong></p>
            <ul>${aiAnalysis?.suspiciousPatterns?.map(p => `<li>${p}</li>`)?.join('')}</ul>
          ` : ''}
        `
      });
    } catch (error) {
      console.error('Error sending variance alert:', error);
    }
  };

  const handleReconciliationComplete = () => {
    loadData();
  };

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 pb-24 md:pb-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Calculator" size={32} className="text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Cash Drawer Reconciliation</h1>
          </div>
          <p className="text-muted-foreground">Reconcile cash drawers with variance alerts and AI analysis</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card rounded-lg shadow-md p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="FileCheck" size={20} className="text-blue-500" />
              <span className="text-2xl font-bold text-foreground">{stats?.totalReconciliations}</span>
            </div>
            <p className="text-sm text-muted-foreground">Total Reconciliations</p>
          </div>
          <div className="bg-card rounded-lg shadow-md p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="TrendingDown" size={20} className="text-orange-500" />
              <span className="text-2xl font-bold text-foreground">${stats?.totalVariance?.toFixed(2)}</span>
            </div>
            <p className="text-sm text-muted-foreground">Total Variance</p>
          </div>
          <div className="bg-card rounded-lg shadow-md p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="AlertTriangle" size={20} className="text-red-500" />
              <span className="text-2xl font-bold text-foreground">{stats?.pendingAlerts}</span>
            </div>
            <p className="text-sm text-muted-foreground">Pending Alerts</p>
          </div>
          <div className="bg-card rounded-lg shadow-md p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="BarChart3" size={20} className="text-green-500" />
              <span className="text-2xl font-bold text-foreground">${stats?.averageVariance?.toFixed(2)}</span>
            </div>
            <p className="text-sm text-muted-foreground">Average Variance</p>
          </div>
        </div>

        {/* Settings */}
        <div className="bg-card rounded-lg shadow-md p-4 border border-border mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="Settings" size={20} className="text-primary" />
            <h2 className="text-lg font-bold text-foreground">Alert Settings</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Variance Threshold ($)
              </label>
              <Input
                type="number"
                value={varianceThreshold}
                onChange={(e) => setVarianceThreshold(parseFloat(e?.target?.value) || 0)}
                placeholder="5.00"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Alert when variance exceeds this amount
              </p>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoAlertEnabled}
                  onChange={(e) => setAutoAlertEnabled(e?.target?.checked)}
                  className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
                />
                <span className="text-sm font-medium text-foreground">Enable Automatic Alerts</span>
              </label>
              <p className="text-xs text-muted-foreground mt-1">
                Automatically send email alerts for variances
              </p>
            </div>
          </div>
        </div>

        {/* Variance Alerts */}
        <VarianceAlerts 
          reconciliations={reconciliations?.filter(r => {
            const variance = Math.abs(parseFloat(r?.actual_balance || 0) - parseFloat(r?.expected_balance || 0));
            return variance > varianceThreshold;
          })}
          threshold={varianceThreshold}
          onRefresh={loadData}
        />

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cash Drawer Manager */}
          <CashDrawerManager
            drawers={drawers}
            activeDrawer={activeDrawer}
            onDrawerChange={setActiveDrawer}
            onUpdate={loadDrawers}
          />

          {/* Reconciliation Form */}
          <ReconciliationForm
            activeDrawer={activeDrawer}
            varianceThreshold={varianceThreshold}
            onComplete={handleReconciliationComplete}
          />
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default CashDrawerReconciliationCenter;